/* eslint-disable jsx-a11y/tabindex-no-positive */
/* eslint-disable react/no-danger, react/jsx-no-target-blank */
import * as React from 'react';
import { Link, Redirect, RouteComponentProps } from 'react-router-dom';
import { Field, SubmissionError } from 'redux-form';
import { connect, ConnectedProps } from 'react-redux';
import cn from 'classnames';
import { get, toLower } from 'lodash';
import { Tabs } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';

import { AdminReducerState } from 'admin/reducers';
import { selectUserInfo } from 'admin/reducers/user/selectors';

import { auth, init } from 'admin/actions';

import t from 'utils/translate';
import * as constants from 'admin/constants';
import { RESET_PASSWORD_PAGE } from 'admin/constants/routes';
import Button from 'admin/components/common/Button';
import { Column } from 'admin/components/common/Grid';
import { TextField } from 'admin/components/common/Form';
import Text from 'admin/components/common/Text';
import LoginLayout from 'admin/components/common/LoginLayout';
import { isMatching, QUERY } from 'admin/components/common/Responsive';
import { selectLoadingStateByName } from 'admin/reducers/loading/selectors';
import { tid, TID } from 'common/utils/test-ids';

import Loader from 'src/common/components/Loader';
import { getResponseErrorMsg } from 'common/resources/api-response';
import { getOrganizationLoginDiscovery, startOrganizationSSO } from 'admin/queries/auth/sso-login';
import LoginForm, { LoginFormValues } from './LoginForm';
import SingleSignOnForm, { SingleSignOnFormValues } from './SingleSignOnForm';
import TwoFACodeForm, { TwoFACodeFormValues } from './TwoFACodeForm';

import css from './Login.scss';

type Props = ConnectedProps<typeof connector> & RouteComponentProps;

type State = {
	isPasswordShown: boolean;
	isTwoFACoderRequired: boolean;
	email: string;
	password: string;
	organizationSlug: string;
	isOrganizationSlugDisabled: boolean;
	ssoStatus: 'idle' | 'in-progress' | 'fail' | 'success';
	ssoErrorMessage: string;
};

class Login extends React.PureComponent<Props, State> {
	constructor(props: Props) {
		super(props);

		const urlSearchParams = new URLSearchParams(props.location.search);
		const orgParam = urlSearchParams.get('org');

		this.state = {
			isPasswordShown: false,
			isTwoFACoderRequired: false,
			email: '',
			password: '',
			organizationSlug: orgParam || '',
			isOrganizationSlugDisabled: !!orgParam,
			ssoStatus: 'idle',
			ssoErrorMessage: '',
		};
	}

	onLoginFormSubmit = async (values: LoginFormValues) => {
		let errorMessage = t('responseError.badRequest');
		const data = {
			email: toLower(values.email).trim(),
			password: values.password,
			organizationSlug: values.organizationSlug,
		};

		const response = await this.props.login(data);

		if (response.errors) {
			if (response.errors[0].message.includes('Invalid credentials')) {
				errorMessage = t('login.unauthorized');
			}

			throw new SubmissionError({ _error: errorMessage });
		} else if (response.result?.twofactor) {
			this.setState({
				isTwoFACoderRequired: true,
				email: values.email,
				password: values.password,
				organizationSlug: values.organizationSlug,
			});
		} else {
			await this.props.init(true);
		}
	};

	onTwoFACodeFormSubmit = async (values: TwoFACodeFormValues) => {
		let errorMessage = t('responseError.badRequest');
		const data = {
			email: this.state.email,
			password: this.state.password,
			organizationSlug: this.state.organizationSlug,
			code: values.code,
		};

		const response = await this.props.login(data);

		if (response.errors) {
			if (response.errors[0].message.includes('Invalid credentials')) {
				errorMessage = t('login.unauthorized');
			}

			throw new SubmissionError({ _error: errorMessage });
		} else {
			await this.props.init(true);
		}
	};

	onSingleSignOnFormSubmit = async (values: SingleSignOnFormValues) => {
		const organizationSlug = toLower(values.organizationName).trim();

		this.setState({
			ssoStatus: 'in-progress',
			organizationSlug,
		});

		try {
			const discovery = await getOrganizationLoginDiscovery(organizationSlug);

			if (!discovery?.exists || !discovery.login?.ssoEnabled) {
				this.setState({ ssoStatus: 'fail' });
				return;
			}

			this.setState({ ssoStatus: 'success' });

			const sso = await startOrganizationSSO(organizationSlug);

			if (!sso?.url) {
				this.setState({ ssoStatus: 'fail' });
				return;
			}

			window.location.assign(sso.url);
		} catch (error) {
			this.setState({
				ssoStatus: 'fail',
				ssoErrorMessage: getResponseErrorMsg(error as IBackendResponseError),
			});
		}
	};

	onSSOTryAgain = () => {
		this.setState({ ssoStatus: 'idle', ssoErrorMessage: '' });
	};

	onShowPsdClick = () => {
		this.setState(state => ({ isPasswordShown: !state.isPasswordShown }));
	};

	renderLoginForm = (): React.ReactNode => {
		const { isBusy } = this.props;
		const { isPasswordShown, isOrganizationSlugDisabled } = this.state;

		return (
			<>
				<div className={css.fields}>
					<Field
						name="email"
						component={TextField}
						isLabelUppercase={false}
						autoComplete="username"
						label={t('login.email.placeholder')}
						isRequired
						tabIndex={2}
						{...tid(TID['login-email-input'])}
					/>
					<div className={css.passwordField}>
						<Field
							name="password"
							component={TextField}
							isLabelUppercase={false}
							autoComplete="current-password"
							type={isPasswordShown ? 'text' : 'password'}
							label={t('login.password.placeholder')}
							isRequired
							tabIndex={3}
							{...tid(TID['login-password-input'])}
						/>
						<button
							className={css.showPasswordBtn}
							type="button"
							onClick={this.onShowPsdClick}
							tabIndex={4}
						>
							{isPasswordShown ? 'Hide' : 'Show'}
						</button>
					</div>
					<Field
						className={css.orgFieldInput}
						name="organizationSlug"
						component={TextField}
						isLabelUppercase={false}
						autoComplete="off"
						label={t('login.organizationSlug.placeholder')}
						disabled={isOrganizationSlugDisabled}
						isRequired
						tabIndex={1}
						{...tid(TID['login-organization-slug-input'])}
					/>
				</div>
				<div className={css.buttons}>
					<Button type="submit" loading={isBusy} disabled={isBusy} tabIndex={5} {...tid(TID['login-button'])}>
						{t('login.submit')}
					</Button>

					<Link to={RESET_PASSWORD_PAGE} className={cn(css.link, isBusy && css.disabled)} tabIndex={6}>
						<Text size={Text.size.label}>{t('login.forgotPassword')}</Text>
					</Link>
				</div>
			</>
		);
	};

	renderLoginContent = (): React.ReactNode => {
		const { submitError } = this.props;
		const { organizationSlug } = this.state;
		const isNoDesktop = isMatching(QUERY.NO_DESKTOP);

		const loginInitialValues = {
			organizationSlug,
		};

		const ssoInitialValues = {
			organizationName: organizationSlug,
		};

		return (
			<>
				<div className={css.hero}>
					<Text
						tag="h1"
						compact
						size={isNoDesktop ? Text.size.subheading : Text.size.heading}
						className={css.heroTitle}
					>
						{t('login.title')}
					</Text>

					<Tabs
						className={css.loginTabs}
						items={[
							{
								key: 'email',
								label: t('login.tabs.email'),
								children: (
									<>
										{submitError && (
											<Text size={Text.size.label} className={css.heroError}>
												{submitError}
												<div className={css.errorIcon}>!</div>
											</Text>
										)}
										<LoginForm
											className={css.form}
											onSubmit={this.onLoginFormSubmit}
											initialValues={loginInitialValues}
										>
											{this.renderLoginForm()}
										</LoginForm>
										<Column justifyContent="center">
											<Text size={Text.size.label}>
												Don&apos;t have an account?{' '}
												<a tabIndex={6} href="https://storycards.com/pricing">
													Sign up
												</a>
											</Text>
										</Column>
									</>
								),
							},
							{
								key: 'sso',
								label: t('login.tabs.sso'),
								children: (
									<div className={css.ssoTab}>
										<Text size={Text.size.label}>{t('login.sso.description')}</Text>
										<SingleSignOnForm
											className={css.form}
											onSubmit={this.onSingleSignOnFormSubmit}
											initialValues={ssoInitialValues}
										>
											<div className={css.fields}>
												<Field
													name="organizationName"
													component={TextField}
													isLabelUppercase={false}
													autoComplete="organization"
													type="text"
													label={t('login.sso.organizationName.label')}
													placeholder={t('login.sso.organizationName.placeholder')}
													isRequired
												/>
												<Text size={Text.size.label} color="black-mid">
													<div
														dangerouslySetInnerHTML={{
															__html: t('login.sso.organizationName.hint', {
																organization:
																	'<strong style="color: rgba(0, 0, 0, 0.6)">acme</strong>',
																organizationUrl:
																	'<strong style="color: rgba(0, 0, 0, 0.6)">my.story-cards.com/acme</strong>',
															}),
														}}
													/>
												</Text>
											</div>
											<div className={css.buttons}>
												<Button type="submit">{t('login.sso.continue')}</Button>
											</div>
										</SingleSignOnForm>
										<Column justifyContent="center">
											<Text size={Text.size.label}>
												Need help?{' '}
												<a tabIndex={6} href="#">
													Contact your workspace admin
												</a>
											</Text>
										</Column>
									</div>
								),
							},
						]}
					/>
				</div>
			</>
		);
	};

	renderTwoFACodeContent = (): React.ReactNode => {
		const { isBusy } = this.props;

		return (
			<>
				<div className={css.hero}>
					<div className={css.heroTitle}>
						<Text tag="h1" compact size={Text.size.heading}>
							{t('login.twoFactor.title')}
						</Text>
						<Text compact size={Text.size.label} className={css.heroSubtitle}>
							{t('login.twoFactor.subtitle')}
						</Text>
					</div>
					<TwoFACodeForm className={css.form} onSubmit={this.onTwoFACodeFormSubmit}>
						<div className={css.fields}>
							<Field
								name="code"
								component={TextField}
								isLabelUppercase={false}
								autoComplete="off"
								label={t('login.twoFactor.code')}
								isRequired
							/>
						</div>
						<div className={css.buttons}>
							<Button type="submit" loading={isBusy} disabled={isBusy}>
								{t('login.submit')}
							</Button>
						</div>
					</TwoFACodeForm>
					<Column justifyContent="center">
						<Text size={Text.size.label}>
							{t('login.twoFactor.needRecover')}{' '}
							<a href="https://storycards.com/recovery">{t('login.twoFactor.tryRecovery')}</a>
						</Text>
					</Column>
				</div>
			</>
		);
	};

	renderSSOContent = (): React.ReactNode => {
		const { ssoStatus, ssoErrorMessage } = this.state;

		switch (ssoStatus) {
			case 'idle':
				return null;

			case 'in-progress':
				return (
					<div className={css.hero}>
						<div className={css.ssoFlow}>
							<div className={css.ssoFlowVisual}>
								<Loader color="rgb(0, 121, 93)" />
							</div>
							<Text tag="h2" compact size={Text.size.subheading} weight={Text.weight.bold}>
								{t('login.sso.status.inProgress.title')}
							</Text>
							<Text size={Text.size.paragraph} color="black-mid">
								{t('login.sso.status.inProgress.description')}
							</Text>
						</div>
					</div>
				);

			case 'success':
				return (
					<div className={css.hero}>
						<div className={css.ssoFlow}>
							<div className={css.ssoFlowVisual}>
								<CheckCircleFilled
									className={cn(css.ssoFlowIcon, css.ssoFlowIconSuccess)}
									aria-hidden
								/>
							</div>
							<Text tag="h2" compact size={Text.size.subheading} weight={Text.weight.bold}>
								{t('login.sso.status.success.title')}
							</Text>
							<Text size={Text.size.paragraph} color="black-mid">
								{t('login.sso.status.success.description')}
							</Text>
						</div>
					</div>
				);

			case 'fail':
				return (
					<div className={css.hero}>
						<div className={css.ssoFlow}>
							<div className={css.ssoFlowVisual}>
								<CloseCircleFilled className={cn(css.ssoFlowIcon, css.ssoFlowIconFail)} aria-hidden />
							</div>
							<Text tag="h2" compact size={Text.size.subheading} weight={Text.weight.bold}>
								{t('login.sso.status.fail.title')}
							</Text>
							<Text size={Text.size.paragraph} color="black-mid">
								{ssoErrorMessage || t('login.sso.status.fail.description')}
							</Text>
							<div className={css.ssoFlowActions}>
								<Button type="button" view="secondary-gray" size="small" onClick={this.onSSOTryAgain}>
									{t('login.sso.status.tryAgain')}
								</Button>
							</div>
						</div>
					</div>
				);

			default: {
				const _exhaustive: never = ssoStatus;
				return _exhaustive;
			}
		}
	};

	render() {
		const { location } = this.props;
		const { isTwoFACoderRequired, ssoStatus } = this.state;
		const from = get(location, 'state.from', { pathname: constants.routes.ROOT });

		if (this.props.userInfo.email && !this.props.isBusy) {
			return <Redirect to={from} />;
		}

		return (
			<LoginLayout>
				{ssoStatus === 'idle'
					? isTwoFACoderRequired === false
						? this.renderLoginContent()
						: this.renderTwoFACodeContent()
					: this.renderSSOContent()}
			</LoginLayout>
		);
	}
}

const mapState = (state: AdminReducerState) => ({
	isBusy: selectLoadingStateByName(state, constants.actions.INIT.PENDING),
	submitError: state.form[`${constants.common.FORM_MODEL.LOGIN}`]?.error || '',
	userInfo: selectUserInfo(state),
});

const mapDispatch = {
	login: auth.post.login,
	init,
};

const connector = connect(mapState, mapDispatch);

export default connector(Login);
